"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.default = void 0;
var _lodash = _interopRequireDefault(require("lodash.isequal"));
var _constants = require("../constants");
var _BASE = _interopRequireDefault(require("./BASE"));
function _interopRequireDefault(obj) {
  return obj && obj.__esModule ? obj : {
    default: obj
  };
}
const GEOJSON_DESCRIPTION = _constants.FILE_TYPE_DESCRIPTIONS[_constants.KEY_GEOJSON];
const isDefined = v => v !== undefined && v !== null;
const isDocumentPropertyValid = doc => {
  // handle undefined
  if (doc === undefined) {
    return true;
  }

  // handle wrong data type
  if (typeof doc !== 'object') {
    return false;
  }

  // check root props
  const hasRequiredRootProps = doc.type === 'FeatureCollection' && (doc.features === undefined || doc.features instanceof Array);
  if (!hasRequiredRootProps) {
    return false;
  }

  // no features are ok, just means empty doc
  const hasFeatures = doc.features instanceof Array && doc.features.length > 0;
  if (!hasFeatures) {
    return true;
  }

  // check first feature for format, if passes assume following features will pass
  const firstFeature = doc.features[0];
  return firstFeature.type === 'Feature' && (firstFeature.properties === undefined || typeof firstFeature.properties === 'object') && (firstFeature.geometry === undefined || typeof firstFeature.geometry === 'object');
};
const isValidFileName = fname => {
  const parts = (fname || '').trim().split('.');
  return parts.length > 1 && parts[parts.length - 1].toLowerCase() === 'geojson';
};
const GEOJSONDocType = {
  ..._BASE.default,
  toFileText: function () {
    let {
      document: doc = {}
    } = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
    return JSON.stringify(doc);
  },
  // @required overwrite
  getFileParams: docJson => {
    if (GEOJSONDocType.isValidDocJson(docJson)) {
      const text = GEOJSONDocType.toFileText(docJson);
      const name = GEOJSONDocType.getFileName(docJson);
      const ext = GEOJSONDocType.getFileExtension(docJson);
      const filename = [name, ext].filter(v => !!v).join('.');
      return {
        filename,
        content: text,
        mimeType: 'application/geo+json'
      };
    }
    return undefined;
  },
  // @required overwrite
  toDocJson: _ref => {
    let {
      content,
      fileName
    } = _ref;
    let geoJson;
    if (isDefined(content)) {
      geoJson = typeof content === 'string' ? JSON.parse(content) : content;
    }
    let fname = fileName;
    if (!isDefined(fileName)) {
      fname = [geoJson.name, 'geojson'].filter(v => !!v).join('.');
    }
    if (isValidFileName(fname)) {
      const parts = (fname || '').trim().split('.');
      const ext = parts.splice(parts.length - 1, 1)[0].toLowerCase();
      const name = parts.join('.').trim();
      return {
        type: _constants.KEY_GEOJSON,
        fileName: name,
        fileExtension: ext,
        document: typeof geoJson === 'string' ? JSON.parse(geoJson) : geoJson
      };
    }
    return undefined;
  },
  isValidDocJson: function () {
    let docJson = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
    const docType = (GEOJSONDocType.getDocType(docJson) || '').toUpperCase();
    const fileExtension = (GEOJSONDocType.getFileExtension(docJson) || '').toLowerCase();
    const doc = GEOJSONDocType.getDocument(docJson);
    return _BASE.default.isValidDocJson(docJson) && docType === GEOJSON_DESCRIPTION.fileType && fileExtension === GEOJSON_DESCRIPTION.fileExtension && isDocumentPropertyValid(doc);
  },
  createFeatureDocId: (feature, _ref2) => {
    let {
      prop,
      props
    } = _ref2;
    let idProps;
    if (prop !== undefined) {
      idProps = [prop];
    } else if (props !== undefined) {
      idProps = props; // assume an array of props
    }
    const {
      properties
    } = feature;
    return idProps.map(p => properties[p]).join('~');
  },
  createFeatureMap: function () {
    let {
      document: doc = {}
    } = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
    let {
      idProp,
      idProps
    } = arguments.length > 1 ? arguments[1] : undefined;
    const {
      features = []
    } = doc;
    return features.reduce((acc, feature) => {
      const docId = GEOJSONDocType.createFeatureDocId(feature, {
        prop: idProp,
        props: idProps
      });
      acc[docId] = feature;
      return acc;
    }, {});
  },
  computeDiffFeatureSets: (docJsonA, docJsonB, _ref3) => {
    let {
      idProp,
      idProps
    } = _ref3;
    const featureMapA = GEOJSONDocType.createFeatureMap(docJsonA, {
      idProp,
      idProps
    });
    const docIdsA = Object.keys(featureMapA);
    const featureMapB = GEOJSONDocType.createFeatureMap(docJsonB, {
      idProp,
      idProps
    });
    const docIdsB = Object.keys(featureMapB);
    const {
      deletedFeatures,
      sharedIds
    } = docIdsA.reduce((acc, idA) => {
      if (featureMapB[idA] === undefined) {
        acc.deletedFeatures.push(featureMapA[idA]);
      } else {
        acc.sharedIds.push(idA);
      }
      return acc;
    }, {
      deletedFeatures: [],
      sharedIds: []
    });
    const addedFeatures = docIdsB.reduce((acc, idB) => {
      if (featureMapA[idB] === undefined) {
        acc.push(featureMapB[idB]);
      }
      return acc;
    }, []);
    const modifiedFeatures = sharedIds.reduce((acc, id) => {
      if (!(0, _lodash.default)(featureMapA[id].properties, featureMapB[id].properties)) {
        acc.push(featureMapB[id]);
      }
      return acc;
    }, []);
    return {
      deleted: deletedFeatures,
      modified: modifiedFeatures,
      added: addedFeatures
    };
  },
  computeDiffDocsByFeatureSets: (docJsonA, docJsonB, _ref4) => {
    let {
      idProp,
      idProps
    } = _ref4;
    const fileNameA = docJsonA.fileName;
    const docNameA = (docJsonA.document || {}).name || fileNameA;
    const fileNameB = docJsonB.fileName;
    const docNameB = (docJsonB.document || {}).name || fileNameB;
    const fileNameDeleted = `DELETED-FROM-${fileNameA}-TO-${fileNameB}.geojson`;
    const docNameDeleted = `DELETED-FROM-${docNameA}-TO-${docNameB}`;
    const fileNameModified = `MODIFIED-FROM-${fileNameA}-TO-${fileNameB}.geojson`;
    const docNameModified = `MODIFIED-FROM-${docNameA}-TO-${docNameB}`;
    const fileNameAdded = `ADDED-FROM-${fileNameA}-TO-${fileNameB}.geojson`;
    const docNameAdded = `ADDED-FROM-${docNameA}-TO-${docNameB}`;
    const featureDiffs = GEOJSONDocType.computeDiffFeatureSets(docJsonA, docJsonB, {
      idProp,
      idProps
    });
    const docJsonDeleted = GEOJSONDocType.toDocJson({
      fileName: fileNameDeleted,
      content: {
        type: docJsonB.document.type,
        name: docNameDeleted,
        features: featureDiffs.deleted
      }
    });
    const docJsonModified = GEOJSONDocType.toDocJson({
      fileName: fileNameModified,
      content: {
        type: docJsonB.document.type,
        name: docNameModified,
        features: featureDiffs.modified
      }
    });
    const docJsonAdded = GEOJSONDocType.toDocJson({
      fileName: fileNameAdded,
      content: {
        type: docJsonB.document.type,
        name: docNameAdded,
        features: featureDiffs.added
      }
    });
    return {
      deleted: docJsonDeleted,
      modified: docJsonModified,
      added: docJsonAdded
    };
  }
};
var _default = GEOJSONDocType;
exports.default = _default;